import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Group, Loader, Select, Text, TextInput, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import HttpClient from '../helpers/HttpClient';
import { toDateOnlyString } from '../helpers/dateOnly';
import { Person } from '../types/person.type';
import { useUserContext } from '../providers/UserContext';
import { notify } from '../components/NotificationUtils';
import { useLanguage } from '../providers/LanguageContext';

type MemberFields = {
  phone1: string;
  phone2: string;
  birthDate: Date | null;
  iban: string;
  socialInsuranceNumber: string;
  shirtSize: string;
  trouserSize: string;
  shoeSize: string;
  emergencyContact: string;
  emergencyPhone: string;
};

function MemberProfile() {
  const { currentPerson, myChildren, refreshUserRights } = useUserContext();
  const { t } = useLanguage();
  const mp = t.memberProfile;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);

  const form = useForm<MemberFields>({
    initialValues: {
      phone1: '',
      phone2: '',
      birthDate: null,
      iban: '',
      socialInsuranceNumber: '',
      shirtSize: '',
      trouserSize: '',
      shoeSize: '',
      emergencyContact: '',
      emergencyPhone: '',
    },
  });

  useEffect(() => {
    if (!currentPerson) return;
    setSelectedPersonId((prev) => prev ?? currentPerson.id);
  }, [currentPerson]);

  useEffect(() => {
    if (!selectedPersonId) return;
    setLoading(true);
    new HttpClient<Person>(`/person/${selectedPersonId}/self-service`)
      .getSingle()
      .then((person) => {
        if (!person) return;
        form.setValues({
          phone1: person.phone1 ?? '',
          phone2: person.phone2 ?? '',
          birthDate: person.birthDate ? new Date(person.birthDate) : null,
          iban: person.iban ?? '',
          socialInsuranceNumber: person.socialInsuranceNumber ?? '',
          shirtSize: person.shirtSize ?? '',
          trouserSize: person.trouserSize ?? '',
          shoeSize: person.shoeSize ?? '',
          emergencyContact: person.emergencyContact ?? '',
          emergencyPhone: person.emergencyPhone ?? '',
        });
      })
      .finally(() => setLoading(false));
  }, [selectedPersonId]);

  const viewedPerson = useMemo(() => {
    if (selectedPersonId === currentPerson?.id) return currentPerson;
    return myChildren.find((c) => c.id === selectedPersonId) ?? null;
  }, [selectedPersonId, currentPerson, myChildren]);

  const onSubmit = async (values: MemberFields) => {
    if (!selectedPersonId) return;
    setSaving(true);
    try {
      const client = new HttpClient<Person>('/person');
      await client.patchAction(`${selectedPersonId}/self-service`, {
        ...values,
        birthDate: values.birthDate ? toDateOnlyString(values.birthDate) : null,
      });
      await refreshUserRights();
      notify(mp.saveSuccess, mp.saveSuccessDesc, 'green');
    } catch {
      notify(mp.saveError, mp.saveErrorDesc, 'red');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !currentPerson) return <Loader size="md" />;

  return (
    <Box maw={480} mt="md">
      <Title order={2} mb="xs">{mp.title}</Title>
      <Text c="dimmed" mb="md">{viewedPerson?.firstName} {viewedPerson?.lastName}</Text>

      {myChildren.length > 0 && (
        <Select
          label={mp.viewingAs}
          data={[
            { value: String(currentPerson.id), label: `${currentPerson.firstName} ${currentPerson.lastName} (${mp.myself})` },
            ...myChildren.map((c) => ({ value: String(c.id), label: `${c.firstName} ${c.lastName}` })),
          ]}
          value={selectedPersonId ? String(selectedPersonId) : null}
          onChange={(val) => val && setSelectedPersonId(Number(val))}
          mb="md"
          allowDeselect={false}
        />
      )}

      <form onSubmit={form.onSubmit(onSubmit)}>
        <TextInput label={mp.phone1} {...form.getInputProps('phone1')} mb="xs" />
        <TextInput label={mp.phone2} {...form.getInputProps('phone2')} mb="xs" />
        <DateInput
          clearable
          valueFormat="DD.MM.YYYY"
          label={mp.birthDate}
          value={form.values.birthDate ? new Date(form.values.birthDate) : null}
          onChange={date => form.setFieldValue('birthDate', date ? new Date(date) : null)}
          dateParser={value => {
            const parts = value.split('.');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                return new Date(year, month, day);
              }
            }
            return new Date(value);
          }}
        />
        <TextInput label={mp.iban} {...form.getInputProps('iban')} mb="xs" />
        <TextInput label={mp.socialInsuranceNumber} {...form.getInputProps('socialInsuranceNumber')} mb="xs" />
        <TextInput label={mp.shirtSize} {...form.getInputProps('shirtSize')} mb="xs" />
        <TextInput label={mp.trouserSize} {...form.getInputProps('trouserSize')} mb="xs" />
        <TextInput label={mp.shoeSize} {...form.getInputProps('shoeSize')} mb="xs" />
        <Title order={4} mt="md" mb="xs">{mp.emergencyTitle}</Title>
        <TextInput
          label={mp.emergencyContact}
          placeholder={mp.emergencyContactPlaceholder}
          {...form.getInputProps('emergencyContact')}
          mb="xs"
        />
        <TextInput label={mp.emergencyPhone} type="tel" {...form.getInputProps('emergencyPhone')} mb="xs" />
        <Group mt="md">
          <Button type="submit" loading={saving}>{t.common.save}</Button>
        </Group>
      </form>
    </Box>
  );
}

export default MemberProfile;
